import { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import ActionSettings from '../../../library/SimpleControls/ActionSettings';
import {
    MentorshipFileUploadModal,
    MentorshipManualAddModal,
    MentorshipSystemIdentifiedModal,
    MentorshipWatchlistModal,
} from './MentorshipMentorPoolImports';
import MentorshipQuickMentorProfile from './MentorshipQuickMentorProfile';
import MentorshipService, {
    MENTOR_POOL_CONFIRMATION_STATUSES,
    MENTOR_POOL_EXPERIENCE_BUCKETS,
    MENTOR_POOL_PAGE_SIZE_DEFAULT,
    MENTOR_POOL_SOURCES,
    MentorshipCampusOption,
    MentorshipInviteFeedback,
    MentorshipMentorPoolListQuery,
    MentorshipMentorPoolMember,
    MentorshipTaxonomyTrack,
} from './MentorshipService';

interface MentorshipMentorPoolProps {
    cohortId: number | null;
    disabled?: boolean;
    campuses: MentorshipCampusOption[];
    isGroup?: boolean;
    /** Notify parent Module Settings to refresh pill badge totals after import/remove. */
    onPoolMembershipChange?: () => void;
}

const emptyQuery = (): MentorshipMentorPoolListQuery => ({
    search: '',
    company: '',
    campusId: null,
    confirmationStatus: [],
    includePreInvite: false,
    trackIds: [],
    experience: [],
    source: [],
    page: 1,
    pageSize: MENTOR_POOL_PAGE_SIZE_DEFAULT,
});

const toggleNumber = (values: number[], value: number) =>
    values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

const toggleString = (values: string[], value: string) =>
    values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

const MentorshipMentorPool = ({
    cohortId,
    disabled,
    campuses,
    isGroup,
    onPoolMembershipChange,
}: MentorshipMentorPoolProps) => {
    const [draft, setDraft] = useState<MentorshipMentorPoolListQuery>(emptyQuery());
    const [query, setQuery] = useState<MentorshipMentorPoolListQuery>(emptyQuery());
    const [members, setMembers] = useState<MentorshipMentorPoolMember[]>([]);
    const [tracks, setTracks] = useState<MentorshipTaxonomyTrack[]>([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(MENTOR_POOL_PAGE_SIZE_DEFAULT);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [profileMember, setProfileMember] = useState<MentorshipMentorPoolMember | null>(null);
    const [trackMember, setTrackMember] = useState<MentorshipMentorPoolMember | null>(null);
    const [statusMember, setStatusMember] = useState<MentorshipMentorPoolMember | null>(null);
    const [removeMember, setRemoveMember] = useState<MentorshipMentorPoolMember | null>(null);
    const [trackIds, setTrackIds] = useState<number[]>([]);
    const [statusValue, setStatusValue] = useState('');
    const [statusComment, setStatusComment] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [invitingMemberId, setInvitingMemberId] = useState<number | null>(null);
    const [inviteFeedback, setInviteFeedback] = useState<MentorshipInviteFeedback | null>(null);
    const [showSystemIdentified, setShowSystemIdentified] = useState(false);
    const [showWatchlist, setShowWatchlist] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [showManual, setShowManual] = useState(false);
    const [confirmedTotal, setConfirmedTotal] = useState<number | null>(null);
    const [invitedTotal, setInvitedTotal] = useState<number | null>(null);
    const [declinedTotal, setDeclinedTotal] = useState<number | null>(null);
    const [onboardedTotal, setOnboardedTotal] = useState<number | null>(null);
    const [activeSources, setActiveSources] = useState<string[]>([]);

    const cohortAvailable = cohortId != null && !disabled;
    const poolSummaryLine = useMemo(() => {
        const parts: string[] = [];
        if (confirmedTotal != null) parts.push(`${confirmedTotal} Confirmed`);
        if (invitedTotal != null) parts.push(`${invitedTotal} Invited`);
        if (declinedTotal != null) parts.push(`${declinedTotal} Declined`);
        if (onboardedTotal != null && onboardedTotal > 0) parts.push(`${onboardedTotal} Onboarded`);
        if (activeSources.length > 0) parts.push(`Sources: ${activeSources.join(', ')}`);
        return parts.length > 0 ? parts.join(' · ') : null;
    }, [confirmedTotal, invitedTotal, declinedTotal, onboardedTotal, activeSources]);

    const loadTracks = async (id: number) => {
        const taxonomy = await MentorshipService.getDomainTaxonomy(id);
        setTracks(taxonomy.tracks || []);
    };

    const loadList = async (id: number, nextQuery: MentorshipMentorPoolListQuery) => {
        setIsLoading(true);
        setErrorMessage('');
        try {
            const result = await MentorshipService.listMentorPool(id, nextQuery);
            setMembers(result.members);
            setPage(result.pagination.page);
            setPageSize(result.pagination.pageSize);
            setTotal(result.pagination.total);
            setTotalPages(result.pagination.totalPages);
            setSelectedIds([]);
        } catch (error) {
            setMembers([]);
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    };

    const loadStatusSummary = async (id: number) => {
        try {
            const [confirmed, invited, declined, onboarded, ...sourceRows] = await Promise.all([
                MentorshipService.listMentorPool(id, {
                    confirmationStatus: ['Confirmed'],
                    page: 1,
                    pageSize: 1,
                }),
                MentorshipService.listMentorPool(id, {
                    confirmationStatus: ['Invited'],
                    page: 1,
                    pageSize: 1,
                }),
                MentorshipService.listMentorPool(id, {
                    confirmationStatus: ['Declined'],
                    page: 1,
                    pageSize: 1,
                }),
                MentorshipService.listMentorPool(id, {
                    confirmationStatus: ['Onboarded'],
                    page: 1,
                    pageSize: 1,
                }),
                ...MENTOR_POOL_SOURCES.map(async (source) => {
                    const result = await MentorshipService.listMentorPool(id, {
                        source: [source],
                        page: 1,
                        pageSize: 1,
                    });
                    return { source, total: result.pagination.total };
                }),
            ]);
            setConfirmedTotal(confirmed.pagination.total);
            setInvitedTotal(invited.pagination.total);
            setDeclinedTotal(declined.pagination.total);
            setOnboardedTotal(onboarded.pagination.total);
            setActiveSources(
                sourceRows.filter((row) => row.total > 0).map((row) => row.source)
            );
        } catch {
            setConfirmedTotal(null);
            setInvitedTotal(null);
            setDeclinedTotal(null);
            setOnboardedTotal(null);
            setActiveSources([]);
        }
    };

    useEffect(() => {
        if (cohortId == null || disabled) {
            setMembers([]);
            setTracks([]);
            setSelectedIds([]);
            setProfileMember(null);
            setInviteFeedback(null);
            setDraft(emptyQuery());
            setQuery(emptyQuery());
            setTotal(0);
            setTotalPages(0);
            setConfirmedTotal(null);
            setInvitedTotal(null);
            setDeclinedTotal(null);
            setOnboardedTotal(null);
            setActiveSources([]);
            return;
        }
        const next = { ...emptyQuery() };
        setDraft(next);
        setQuery(next);
        setProfileMember(null);
        loadTracks(cohortId).catch((error) => setErrorMessage(MentorshipService.getErrorMessage(error)));
        loadList(cohortId, next);
        loadStatusSummary(cohortId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cohortId, disabled]);

    const applyDraftFilters = () => {
        if (cohortId == null) return;
        const next = { ...draft, page: 1, pageSize };
        setQuery(next);
        loadList(cohortId, next);
    };

    const loadPage = (nextPage: number, nextPageSize = pageSize) => {
        if (cohortId == null) return;
        const next = { ...query, page: nextPage, pageSize: nextPageSize };
        setQuery(next);
        loadList(cohortId, next);
    };

    const refresh = () => {
        if (cohortId == null) return;
        loadList(cohortId, query);
        loadStatusSummary(cohortId);
        onPoolMembershipChange?.();
    };

    const visibleIds = useMemo(() => members.map((item) => item.id), [members]);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

    const toggleVisible = () => {
        if (allVisibleSelected) {
            setSelectedIds((current) => current.filter((id) => !visibleIds.includes(id)));
            return;
        }
        setSelectedIds((current) => Array.from(new Set([...current, ...visibleIds])));
    };

    const saveTracks = async () => {
        if (cohortId == null || trackMember == null) return;
        if (trackIds.length < 1) {
            setErrorMessage('Select at least one career track.');
            return;
        }
        setIsSaving(true);
        setErrorMessage('');
        try {
            await MentorshipService.replaceMentorPoolMemberTracks(cohortId, trackMember.id, trackIds);
            setTrackMember(null);
            refresh();
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const saveStatus = async () => {
        if (cohortId == null || statusMember == null || !statusValue) return;
        setIsSaving(true);
        setErrorMessage('');
        try {
            await MentorshipService.updateMentorPoolMemberStatus(cohortId, statusMember.id, statusValue, statusComment);
            setStatusMember(null);
            refresh();
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const confirmRemove = async () => {
        if (cohortId == null || removeMember == null) return;
        setIsSaving(true);
        setErrorMessage('');
        try {
            await MentorshipService.removeMentorPoolMember(cohortId, removeMember.id);
            setRemoveMember(null);
            setProfileMember((current) => (current?.id === removeMember.id ? null : current));
            refresh();
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const sendInvite = async (member: MentorshipMentorPoolMember) => {
        if (cohortId == null) return;
        setInvitingMemberId(member.id);
        setErrorMessage('');
        setInviteFeedback(null);
        try {
            const result = await MentorshipService.inviteMentorPoolMember(cohortId, member.id);
            const nextStatus = result.confirmationStatus || member.confirmationStatus;
            const nextInvitedAt = result.invitedAt ?? member.invitedAt;
            setInviteFeedback(MentorshipService.formatMentorInviteOutcome(result));
            setMembers((current) =>
                current.map((item) =>
                    item.id === member.id
                        ? {
                              ...item,
                              confirmationStatus: nextStatus,
                              invitedAt: nextInvitedAt,
                          }
                        : item
                )
            );
            setProfileMember((current) =>
                current && current.id === member.id
                    ? {
                          ...current,
                          confirmationStatus: nextStatus,
                          invitedAt: nextInvitedAt,
                      }
                    : current
            );
        } catch (error) {
            setInviteFeedback({
                tone: 'danger',
                text: MentorshipService.getErrorMessage(error),
            });
        } finally {
            setInvitingMemberId(null);
        }
    };

    const sendMessage = (targetMembers: MentorshipMentorPoolMember[]) => {
        const result = MentorshipService.openExistingMessages(
            targetMembers.map((member) => MentorshipService.messageRecipientUuid(member))
        );
        if (!result.success) {
            setErrorMessage(result.message);
            return;
        }
        setErrorMessage('');
    };

    const handleRowAction = (memberId: number, action: string) => {
        const member = members.find((item) => item.id === memberId);
        if (!member) return;
        switch (action) {
            case 'VIEW':
                setProfileMember(member);
                break;
            case 'MESSAGE':
                sendMessage([member]);
                break;
            case 'STATUS':
                setStatusMember(member);
                setStatusValue(member.confirmationStatus || '');
                setStatusComment(member.confirmationComment || '');
                break;
            case 'INVITE':
                if (invitingMemberId !== member.id) sendInvite(member);
                break;
            case 'REMOVE':
                setRemoveMember(member);
                break;
            default:
                break;
        }
    };

    return (
        <div>
            <div className="d-flex flex-wrap align-items-start justify-content-between mb-4 gap-3">
                <div>
                    <div className="font-22 font-600 text-dark">Mentor Pool</div>
                    {poolSummaryLine ? (
                        <div className="font-13 text-secondary">{poolSummaryLine}</div>
                    ) : null}
                </div>
                
                <div className="d-flex gap-2">
                    <button type="button" className="btn btn-outline-secondary btn-sm font-13 font-600 py-2 px-3 rounded-3 rounded-pill" disabled={!cohortAvailable} onClick={() => setShowSystemIdentified(true)}>
                        System Identified
                    </button>
                    <button type="button" className="btn btn-outline-secondary btn-sm font-13 font-600 py-2 px-3 rounded-3 rounded-pill" disabled={!cohortAvailable} onClick={() => setShowWatchlist(true)}>
                        Watchlist
                    </button>
                    <button type="button" className="btn btn-outline-secondary btn-sm font-13 font-600 py-2 px-3 rounded-3 rounded-pill" disabled={!cohortAvailable} onClick={() => setShowUpload(true)}>
                        File Upload
                    </button>
                    <button type="button" className="btn-green btn-sm font-13 font-600 py-2 px-3 rounded-3 rounded-pill" disabled={!cohortAvailable} onClick={() => setShowManual(true)}>
                        <span>+ Add Manually</span>
                    </button>
                    {/* Send Message hidden from the Mentor Pool header (QA). Row-level Message was
                        already hidden in R-QA-01 intake 33; the helper stays for Quick Profile. */}
                </div>
            </div>

            {!cohortAvailable && <div className="text-muted mb-3">Select a cohort to manage Mentor Pool.</div>}
            {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
            {inviteFeedback && (
                <div className={MentorshipService.inviteFeedbackAlertClass(inviteFeedback.tone)}>
                    {inviteFeedback.text}
                </div>
            )}

            <fieldset disabled={!cohortAvailable}>
                <div className="card border-0 rounded-3 p-3 shadow-sm bg-white mb-3">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1">
                            <div className="position-relative" style={{minWidth: 220}}>                                
                                <input
                                    className="form-control form-search font-14 border-light-subtle"
                                    placeholder="Search name, email, or UUID"
                                    value={draft.search || ''}
                                    onChange={(event) => setDraft((current) => ({ ...current, search: event.target.value }))}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            applyDraftFilters();
                                        }
                                    }}
                                />
                            </div>
                            <div className="position-relative" style={{minWidth: 220}}>                                
                                <input
                                    className="form-control form-search font-14 border-light-subtle"
                                    placeholder="Company"
                                    value={draft.company || ''}
                                    onChange={(event) => setDraft((current) => ({ ...current, company: event.target.value }))}
                                />
                            </div>

                            {isGroup && (
                        
                                    <select
                                        className="form-select font-13 w-auto"
                                        value={draft.campusId ?? ''}
                                        onChange={(event) =>
                                            setDraft((current) => ({
                                                ...current,
                                                campusId: event.target.value ? Number(event.target.value) : null,
                                            }))
                                        }>
                                        <option value="">All campuses</option>
                                        {campuses.map((campus) => (
                                            <option key={campus.value} value={campus.value}>
                                                {campus.label}
                                            </option>
                                        ))}
                                    </select>
                            
                            )}

                             <select
                                    className="form-select font-13 w-auto"                                 
                                    value={draft.confirmationStatus || []}
                                    onChange={(event) => {
                                        const selected = Array.from(event.target.selectedOptions).map((opt) => opt.value);
                                        setDraft((current) => ({ ...current, confirmationStatus: selected }));
                                    }}
                                ><option value="">Status</option>
                                    {MENTOR_POOL_CONFIRMATION_STATUSES.map((status) => (
                                        <option key={status} value={status}>
                                            {status}
                                        </option>
                                    ))}
                                    <option key="includePreInvite" value="includePreInvite">
                                            Include pre-invite
                                        </option>
                                </select>
                                {/* <label className="me-3 mt-2 d-block">
                                    <input
                                        type="checkbox"
                                        className="me-1"
                                        checked={Boolean(draft.includePreInvite)}
                                        onChange={(event) => setDraft((current) => ({ ...current, includePreInvite: event.target.checked }))}
                                    />
                                    Include pre-invite
                                </label> */}

                                <select
                                    className="form-select font-13 w-auto"
                                    value={(draft.trackIds || []).map(String)}
                                    onChange={(event) => {
                                        const selected = Array.from(event.target.selectedOptions).map((opt) => Number(opt.value));
                                        setDraft((current) => ({ ...current, trackIds: selected }));
                                    }}
                                >   <option value="">Career Tracks</option>
                                    {tracks
                                        .filter((track) => track.id != null)
                                        .map((track) => (
                                            <option key={track.id} value={track.id as number}>
                                                {track.name}
                                            </option>
                                        ))}
                                </select>

                                <select
                                    className="form-select font-13 w-auto"
                                    value={draft.experience || []}
                                    onChange={(event) => {
                                        const selected = Array.from(event.target.selectedOptions).map((opt) => opt.value);
                                        setDraft((current) => ({ ...current, experience: selected }));
                                    }}
                                ><option value="">Experience</option>
                                    {MENTOR_POOL_EXPERIENCE_BUCKETS.map((item) => (
                                        <option key={item} value={item}>
                                            {item}
                                        </option>
                                    ))}
                                </select>

                                <select
                                    className="form-select font-13 w-auto"
                                    value={draft.source || []}
                                    onChange={(event) => {
                                        const selected = Array.from(event.target.selectedOptions).map((opt) => opt.value);
                                        setDraft((current) => ({ ...current, source: selected }));
                                    }}
                                ><option value="">Source</option>
                                    {MENTOR_POOL_SOURCES.map((item) => (
                                        <option key={item} value={item}>
                                            {item}
                                        </option>
                                    ))}
                                </select>

                                <button type="button" className="btn btn-green btn-sm" onClick={applyDraftFilters}>
                                    <span>
                                        Apply filters
                                    </span>
                                </button>

                                <div className="d-flex align-items-center gap-2 flex-1 justify-content-end">
                                    <span className="font-13 text-secondary">Selected: <strong id="sel-count">0</strong></span>
                                    <button className="btn btn-outline-secondary font-13 font-600 rounded-2"  id="remind-bulk-btn">Send Reminder</button>
                                    <div className="dropdown">
                                    <button className="btn btn-outline-secondary font-13 font-600 rounded-2 dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">Bulk Actions</button>
                                    <ul className="dropdown-menu dropdown-menu-end">
                                        <li><a className="dropdown-item font-13" href="javascript:;" >📧 Email Selected</a></li>
                                        <li><a className="dropdown-item font-13 text-danger" href="javascript:;" >Remove Selected</a></li>
                                    </ul>
                                    </div>
                                </div>

                      
                          
                           
                            
                        </div>
                    </div>
                </div>
                
            </fieldset>

            {isLoading ? (
                <div className="text-muted">Loading Mentor Pool…</div>
            ) : members.length === 0 ? (
                <div className="text-muted">No mentors in this pool yet.</div>
            ) : (
                <>
                    <div className="card border-0 rounded-3 shadow-sm bg-white overflow-hidden">
                        <div className="table-responsive">
                            <table className="table align-middle mb-0 table-hover" style={{ minWidth: 1920 }}>
                                <thead>
                                    <tr>
                                        <th className="px-4 py-2 bg-light" style={{ width: 40 }}>
                                            <input type="checkbox" checked={allVisibleSelected} onChange={toggleVisible} />
                                        </th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">Alumni Name</th>
                                        {/* UUID column hidden in Mentor Pool UI. Keep the field on the row for search and profile links. */}
                                        {/* <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">UUID</th> */}
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">Program</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">Batch</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">Current Role</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">Career Tracks</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">AP</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">MQ</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">VL</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">Experience</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">Source</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">Status</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 py-2 bg-light">Mentees Assigned</th>
                                        <th className="font-13 text-uppercase text-secondary font-600 px-4 py-2 bg-light">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {members.map((member) => {
                                        const photo = MentorshipService.profilePictureSrc(member.alumni.profilePicture);
                                        const profileUuid = String(
                                            member.alumni.uuid || member.alumniId || ''
                                        ).trim();
                                        const displayName = member.alumni.name || '—';
                                        const profileHref =
                                            profileUuid && member.alumni.name
                                                ? `/alumni/${profileUuid}/profile`
                                                : null;
                                        return (
                                            <tr key={member.id}>
                                                <td className="px-4">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.includes(member.id)}
                                                        onChange={() =>
                                                            setSelectedIds((current) =>
                                                                current.includes(member.id)
                                                                    ? current.filter((id) => id !== member.id)
                                                                    : [...current, member.id]
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {photo ? (
                                                            <img
                                                                src={photo}
                                                                alt=""
                                                                width={32}
                                                                height={32}
                                                                className="rounded-circle font-12 font-500"
                                                                style={{ objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <div
                                                                className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                                                                style={{ width: 32, height: 32, fontSize: 12, fontWeight: 500 }}>
                                                                {(member.alumni.name || '?').slice(0, 1).toUpperCase()}
                                                            </div>
                                                        )}
                                                        {profileHref ? (
                                                            <a
                                                                className="font-13 font-600 text-dark text-decoration-none"
                                                                href={profileHref}
                                                                target="_blank"
                                                                rel="noopener noreferrer">
                                                                {displayName}
                                                            </a>
                                                        ) : (
                                                            <span className="font-13 font-600 text-dark">{displayName}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                {/* <td className="font-13">{member.alumni.uuid || '—'}</td> */}
                                                <td className="font-13">{member.alumni.programName || '—'}</td>
                                                <td className="font-13">{member.alumni.batchYear == null ? '—' : String(member.alumni.batchYear)}</td>
                                                <td className="font-13">{MentorshipService.displayCurrentRole(member.alumni.company, member.alumni.designation)}</td>
                                                <td className="font-13">
                                                    {member.tracks.map((track) => track.name).filter(Boolean).join(', ') || '—'}{' '}
                                                    <button
                                                        type="button"
                                                        className="btn btn-link btn-sm p-0"
                                                        onClick={() => {
                                                            setTrackMember(member);
                                                            setTrackIds(member.tracks.map((track) => track.id).filter((id) => id != null));
                                                        }}>
                                                        Edit
                                                    </button>
                                                </td>
                                                <td className="font-13">{member.alumni.ap == null ? '—' : String(member.alumni.ap)}</td>
                                                <td className="font-13">{member.alumni.mq == null ? '—' : String(member.alumni.mq)}</td>
                                                <td className="font-13">{member.alumni.vl == null ? '—' : String(member.alumni.vl)}</td>
                                                <td className="font-13">{member.alumni.totalWorkex == null ? '—' : String(member.alumni.totalWorkex)}</td>
                                                <td className="font-13">{member.source || '—'}</td>
                                                <td className="font-13">{MentorshipService.displayConfirmationStatus(member.confirmationStatus)}</td>
                                                <td className="font-13">{MentorshipService.displayAssignedMentees(member.assignedMentees)}</td>
                                                <td>
                                                    <ActionSettings
                                                        id={member.id}
                                                        actions={
                                                            invitingMemberId === member.id
                                                                ? ['View', 'Status', 'Inviting', 'Remove']
                                                                : ['View', 'Status', 'Invite', 'Remove']
                                                        }
                                                        onClick={handleRowAction}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="card-footer bg-white border-top py-3 px-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <span className="font-12 text-secondary">
                                Page {page} of {totalPages || 1} · {total} mentors · {selectedIds.length} selected on this view
                            </span>

                            <div className="d-flex gap-2">
                            <select
                                className="form-select form-select-sm"
                                style={{ width: 90 }}
                                value={pageSize}
                                onChange={(event) => loadPage(1, Number(event.target.value))}>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                disabled={page <= 1}
                                onClick={() => loadPage(page - 1, pageSize)}>
                                Previous
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                disabled={page >= (totalPages || 1)}
                                onClick={() => loadPage(page + 1, pageSize)}>
                                Next
                            </button>
                        </div>
                        </div>
                    </div>
                    
                    
                </>
            )}

            <MentorshipQuickMentorProfile
                open={profileMember != null}
                member={profileMember}
                onClose={() => setProfileMember(null)}
                onEditTracks={() => {
                    if (!profileMember) return;
                    setTrackMember(profileMember);
                    setTrackIds(profileMember.tracks.map((track) => track.id).filter((id) => id != null));
                }}
                onEditStatus={() => {
                    if (!profileMember) return;
                    setStatusMember(profileMember);
                    setStatusValue(profileMember.confirmationStatus || '');
                    setStatusComment(profileMember.confirmationComment || '');
                }}
                onRemove={() => profileMember && setRemoveMember(profileMember)}
                onSendMessage={() => profileMember && sendMessage([profileMember])}
                onSendInvite={() => profileMember && sendInvite(profileMember)}
                inviting={profileMember != null && invitingMemberId === profileMember.id}
            />

            <Modal show={trackMember != null} onHide={() => setTrackMember(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Career Tracks</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {tracks
                        .filter((track) => track.id != null)
                        .map((track) => (
                            <label key={track.id} className="d-block mb-2">
                                <input
                                    type="checkbox"
                                    className="me-2"
                                    checked={trackIds.includes(track.id as number)}
                                    onChange={() => setTrackIds((current) => toggleNumber(current, track.id as number))}
                                />
                                {track.name}
                            </label>
                        ))}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setTrackMember(null)}>
                        Cancel
                    </Button>
                    <Button variant="success" disabled={isSaving || trackIds.length < 1} onClick={saveTracks}>
                        Save Tracks
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={statusMember != null} onHide={() => setStatusMember(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Update Confirmation Status</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="small text-muted">This updates admin status only. It does not send email.</p>
                    <select className="form-select mb-3" value={statusValue} onChange={(event) => setStatusValue(event.target.value)}>
                        <option value="">Select status</option>
                        {MENTOR_POOL_CONFIRMATION_STATUSES.map((status) => (
                            <option key={status} value={status}>
                                {status}
                            </option>
                        ))}
                    </select>
                    <label className="form-label">Comment (optional)</label>
                    <textarea className="form-control" rows={3} value={statusComment} onChange={(event) => setStatusComment(event.target.value)} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setStatusMember(null)}>
                        Cancel
                    </Button>
                    <Button variant="success" disabled={isSaving || !statusValue} onClick={saveStatus}>
                        Save Status
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={removeMember != null} onHide={() => setRemoveMember(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Remove from Pool</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Remove {removeMember?.alumni.name || 'this mentor'} from the Mentor Pool? The alumni profile is not deleted.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setRemoveMember(null)}>
                        Cancel
                    </Button>
                    <Button variant="danger" disabled={isSaving} onClick={confirmRemove}>
                        Remove
                    </Button>
                </Modal.Footer>
            </Modal>

            <MentorshipSystemIdentifiedModal
                show={showSystemIdentified}
                cohortId={cohortId}
                disabled={!cohortAvailable}
                campuses={campuses}
                tracks={tracks}
                onClose={() => setShowSystemIdentified(false)}
                onImported={refresh}
            />
            <MentorshipWatchlistModal
                show={showWatchlist}
                cohortId={cohortId}
                disabled={!cohortAvailable}
                campuses={campuses}
                tracks={tracks}
                onClose={() => setShowWatchlist(false)}
                onImported={refresh}
            />
            <MentorshipFileUploadModal
                show={showUpload}
                cohortId={cohortId}
                disabled={!cohortAvailable}
                campuses={campuses}
                tracks={tracks}
                onClose={() => setShowUpload(false)}
                onImported={refresh}
            />
            <MentorshipManualAddModal
                show={showManual}
                cohortId={cohortId}
                disabled={!cohortAvailable}
                campuses={campuses}
                tracks={tracks}
                onClose={() => setShowManual(false)}
                onImported={refresh}
            />
        </div>
    );
};

export default MentorshipMentorPool;
